"use client";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { NumericFormat } from "react-number-format";
import Select from "react-select";
import { saveAs } from "file-saver";
import PizZip from "pizzip";
import BudgetImpactPanel from "./BudgetImpactPanel";
import AttachmentsPanel3 from "./AttachmentsPanel3";
import Docxtemplater from "docxtemplater";
import ImageModule from "docxtemplater-image-module-free";
import AlertDialog from "./AlertDialog";
import MaterialOrderTimeline from "./MaterialOrderTimeline";
import BillMOReviewPanel from "./BillMOReviewPanel";
import AddItemsModal3, {
  isCoverageBased,
  parseSizeCoverage,
} from "./AddItemsModal3";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Precision formatter for coverage-based $/SF, $/LF rates — keeps up to
// 4 decimal places so sub-cent rates don't round to $0.00 or $0.01.
const fmtRate = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

const cls = (...c) => c.filter(Boolean).join(" ");
const safeNum = (v, d = 0) => {
  const p = parseFloat(v);
  return isNaN(p) ? d : p;
};

/** Per-PC price = rate × coverage */
const calcPackageCost = (rate, coverage) =>
  parseFloat(rate || 0) * (parseFloat(coverage) || 1);

const selectStyles = {
  control: (p, s) => ({
    ...p,
    backgroundColor: "#374151",
    borderColor: s.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    minHeight: "32px",
    fontSize: "13px",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (p) => ({ ...p, color: "#e5e7eb" }),
  option: (p, s) => ({
    ...p,
    backgroundColor: s.isSelected ? "#4b5563" : "#374151",
    color: s.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#6b7280" },
    fontSize: "13px",
  }),
  menu: (p) => ({
    ...p,
    backgroundColor: "#374151",
    border: "1px solid #4b5563",
    zIndex: 50,
  }),
  singleValue: (p) => ({ ...p, color: "#e5e7eb" }),
  multiValue: (p) => ({ ...p, backgroundColor: "#4b5563" }),
  multiValueLabel: (p) => ({ ...p, color: "#e5e7eb" }),
  multiValueRemove: (p) => ({
    ...p,
    color: "#e5e7eb",
    "&:hover": { backgroundColor: "#f97316", color: "#fff" },
  }),
  placeholder: (p) => ({ ...p, color: "#9ca3af" }),
};

const STATUS_BADGE = {
  pending: "bg-yellow-600/70 text-yellow-100",
  confirmed: "bg-blue-600/70 text-blue-100",
  partial: "bg-orange-600/70 text-orange-100",
  delivered: "bg-green-600/70 text-green-100",
  void: "bg-red-800/70 text-red-200",
};

// ─────────────────────────────────────────────────────────────────────────────
export default function EditMaterialOrder3() {
  const { id: projectId, orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // "default" key = first entry in this tab's history stack (e.g. after a refresh),
  // so navigate(-1) would be a no-op. Fall back to the project's Materials tab.
  const goBack = useCallback(() => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(`/Projects/${projectId}?tab=Materials`, { replace: true });
    }
  }, [location.key, navigate, projectId]);

  const [showBillReviewPanel, setShowBillReviewPanel] = useState(false);
  const [order, setOrder] = useState(null);
  const [project, setProject] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSiteUpdateForm, setShowSiteUpdateForm] = useState(false);
  const [siteUpdateBuffer, setSiteUpdateBuffer] = useState(null);
  const [siteUpdateForm, setSiteUpdateForm] = useState({
    subject: "",
    message: "",
  });
  const [sendingSiteUpdate, setSendingSiteUpdate] = useState(false);
  const [description, setDescription] = useState("");
  const canRevert =
    ["delivered", "partial"].includes(order?.status) &&
    (order?.items || []).some((i) => (i.qtyDelivered || 0) > 0);

  const canReactivate = order?.status === "void";

  const [revision, setRevision] = useState(0);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [fieldContacts, setFieldContacts] = useState([]);
  const [deliveryContact, setDeliveryContact] = useState(null);
  const [shippingMethod, setShippingMethod] = useState("Delivery");
  const [deliveryInstructions, setDeliveryInstructions] = useState("");
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
  const [surcharges, setSurcharges] = useState([]);

  const [deliveryQtys, setDeliveryQtys] = useState({});
  const [varianceReasons, setVarianceReasons] = useState({});
  const [delivering, setDelivering] = useState(false);

  /**
   * lineEdits — keyed by item._id
   * Only stores what the user can change:
   *   size   — editable for SF/LF items only
   *   rate   — $/SF, $/LF, or $/PC depending on UOM
   *   qty    — number of pieces
   *
   * Coverage is NEVER stored here — always derived live from size via
   * parseSizeCoverage, falling back to item.coveragePerUnit if unparseable.
   * Per-PC unitPrice is always computed as rate × coverage.
   */
  const [lineEdits, setLineEdits] = useState({});
  const [savingLine, setSavingLine] = useState(null);
  const [suggestedSurcharges, setSuggestedSurcharges] = useState([]);
  const [showAddItems, setShowAddItems] = useState(false);

  const [userEmail, setUserEmail] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [vendorPdfBuffer, setVendorPdfBuffer] = useState(null);
  const [sitePdfBuffer, setSitePdfBuffer] = useState(null);
  const [emailForm, setEmailForm] = useState({
    to: "",
    subject: "",
    body: "",
    signature: "",
  });
  const [linkedBill, setLinkedBill] = useState(null);
  const [billLoading, setBillLoading] = useState(false);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [showBillViewer, setShowBillViewer] = useState(false);
  const [billAttachmentType, setBillAttachmentType] = useState("sales_order");

  const [alert, setAlert] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
    confirmText: "OK",
    onConfirm: () => {},
  });
  const showAlert = (type, title, message, opts = {}) =>
    setAlert({
      open: true,
      type,
      title,
      message,
      confirmText: opts.confirmText || "OK",
      cancelText: opts.cancelText,
      onConfirm:
        opts.onConfirm || (() => setAlert((p) => ({ ...p, open: false }))),
      onClose: () => setAlert((p) => ({ ...p, open: false })),
    });

  // ── load ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const decoded = jwtDecode(token);
        setUserEmail(decoded.email || "");
        const h = { Authorization: `Bearer ${token}` };

        const [
          orderRes,
          projectRes,
          vendorsRes,
          contactsRes,
          usersRes,
          companyRes,
        ] = await Promise.all([
          axios.get(`${API}/materialOrders3/${orderId}`, { headers: h }),
          axios.get(`${API}/projects/${projectId}`, { headers: h }),
          axios.get(`${API}/accounts`, { headers: h }),
          axios.get(`${API}/contacts`, { headers: h }),
          axios.get(`${API}/users`, { headers: h }),
          axios.get(`${API}/company-profile`, { headers: h }),
        ]);

        const o = orderRes.data.order || orderRes.data;
        const p = projectRes.data;

        setOrder(o);
        // Enrich MO items missing descriptions from ItemDatabase
        const missingItems = (o.items || []).filter((i) => !i.description);
        if (missingItems.length > 0) {
          try {
            const ids = [
              ...new Set(
                missingItems
                  .map((i) => i.itemDatabaseId)
                  .filter(Boolean)
                  .map(String),
              ),
            ];
            const itemCodes = [
              ...new Set(
                missingItems
                  .map((i) => i.itemCode)
                  .filter(Boolean)
                  .map((c) => c.trim()),
              ),
            ];

            const descRes = await axios.post(
              `${API}/materialOrders3/items/descriptions`,
              { ids, itemCodes },
              { headers: h },
            );
            const { byId = {}, byCode = {} } = descRes.data;

            setOrder((prev) => ({
              ...prev,
              items: (prev.items || []).map((item) => {
                if (item.description) return item;
                const idKey =
                  item.itemDatabaseId?.toString?.() ||
                  String(item.itemDatabaseId || "");
                const code = (item.itemCode || "").trim();
                const desc =
                  (idKey && byId[idKey]) || (code && byCode[code]) || "";
                return desc ? { ...item, description: desc } : item;
              }),
            }));
          } catch {}
        }
        setProject(p);
        setVendors((vendorsRes.data || []).filter((v) => v.type === "vendor"));
        setContacts(contactsRes.data || []);
        setUsers(usersRes.data || []);
        setCompanyProfile(companyRes.data);

        setRevision(o.revision || 0);
        setShippingMethod(o.shippingMethod || "Delivery");
        setDescription(o.description || "");
        setDeliveryInstructions(o.deliveryInstructions || "");
        setExpectedDeliveryDate(
          o.expectedDeliveryDate ? o.expectedDeliveryDate.split("T")[0] : "",
        );
        setSurcharges(o.surcharges || []);

        const contactIds = Array.isArray(o.contact)
          ? o.contact.map((c) => c._id || c)
          : [];
        setSelectedContacts(contactIds);
        const fieldIds = Array.isArray(o.fieldContact)
          ? o.fieldContact.map((u) => u._id || u)
          : [];
        setFieldContacts(fieldIds);
        setDeliveryContact(o.deliveryContact?._id || o.deliveryContact || null);
        setDeliveryQtys(
          Object.fromEntries(
            (o.items || []).map((i) => [i._id, i.qtyPending || 0]),
          ),
        );

        seedLineEdits(o.items || []);

        const vendorId = o.vendor?._id || o.vendor;
        const vendorContacts = (contactsRes.data || []).filter(
          (c) => contactIds.includes(c._id) || c.relatedAccount === vendorId,
        );
        const greeting = vendorContacts[0]?.firstName
          ? `Hi ${vendorContacts[0].firstName},`
          : "Dear Vendor,";
        const currentUser = (usersRes.data || []).find(
          (u) => u.email === decoded.email,
        );

        setEmailForm({
          to: vendorContacts
            .map((c) => c.email)
            .filter(Boolean)
            .join(", "),
          subject: `${p.projectName} - PO#${p.projectNumber}-${o.orderNumber}`,
          body: `${greeting}\n\nPlease find attached the material order ${o.orderNumber} for your review and action.\n\nBest regards,`,
          signature:
            currentUser?.displayName || decoded.name || decoded.email || "User",
        });
      } catch (err) {
        showAlert("error", "Load Error", err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId, projectId]);

  // ADD this new useEffect after the load useEffect:
  useEffect(() => {
    if (!contacts.length || !selectedContacts.length) return;
    const vendorContacts = contacts.filter((c) =>
      selectedContacts.map(String).includes(String(c._id)),
    );
    if (!vendorContacts.length) return;
    const greeting = vendorContacts[0]?.firstName
      ? `Hi ${vendorContacts[0].firstName},`
      : "Dear Vendor,";
    setEmailForm((p) => ({
      ...p,
      to: vendorContacts
        .map((c) => c.email)
        .filter(Boolean)
        .join(", "),
      body: `${greeting}\n\nPlease find attached the material order for your review and action.\n\nBest regards,`,
    }));
  }, [selectedContacts, contacts]);

  useEffect(() => {
    if (!projectId || !orderId) return;
    const token = localStorage.getItem("token");
    axios
      .get(`${API}/materialOrders3/project/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        const orders = (r.data.orders || []).sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
        const seen = new Map();
        for (const o of orders) {
          if (String(o._id) === String(orderId)) continue;
          for (const sc of o.surcharges || []) {
            const desc = (sc.description || "").trim();
            const price = safeNum(sc.price ?? sc.amount);
            if (!desc || desc.startsWith("Credit —") || price === 0) continue;
            const key = `${desc.toLowerCase()}|${price}`;
            if (!seen.has(key)) seen.set(key, { description: desc, price });
          }
        }
        setSuggestedSurcharges(Array.from(seen.values()).slice(0, 10));
      })
      .catch(() => {});
  }, [projectId, orderId]);

  useEffect(() => {
    if (!orderId || !projectId) return;
    setBillLoading(true);
    const token = localStorage.getItem("token");
    axios
      .get(`${API}/bills3/project/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        const bills = r.data.bills || [];
        const match = bills.find(
          (b) =>
            b.relatedMO3?._id?.toString() === orderId ||
            b.relatedMO3?.toString() === orderId,
        );
        setLinkedBill(match || null);
      })
      .catch(() => setLinkedBill(null))
      .finally(() => setBillLoading(false));
  }, [orderId, projectId]);

  const handleUploadMOAttachments = async (files, type) => {
    if (!files?.length) return;
    setAttachmentUploading(true);
    try {
      const token = localStorage.getItem("token");

      if (linkedBill) {
        // Attach to the bill
        const formData = new FormData();
        formData.append(
          "existingAttachments",
          JSON.stringify(linkedBill.attachments || []),
        );
        const fieldName =
          type === "sales_order" ? "salesOrderFiles" : "packingSlipFiles";
        for (const f of files) formData.append(fieldName, f);
        const res = await axios.put(
          `${API}/bills3/${linkedBill._id}`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setLinkedBill(res.data.bill);
      } else {
        // No bill — attach directly to the MO
        const formData = new FormData();
        const fieldName =
          type === "sales_order" ? "salesOrderFiles" : "packingSlipFiles";
        for (const f of files) formData.append(fieldName, f);
        const res = await axios.post(
          `${API}/materialOrders3/${orderId}/attachments`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setOrder(res.data.order || res.data);
      }

      showAlert(
        "success",
        "Uploaded",
        `${type === "sales_order" ? "Sales order" : "Packing slip"} attached successfully.`,
      );
    } catch (err) {
      showAlert(
        "error",
        "Upload failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setAttachmentUploading(false);
    }
  };
  // ── lineEdits seed ─────────────────────────────────────────────────────────
  /**
   * Seed rate from stored per-PC unitPrice:
   *   coverage items → rate = unitPrice / coveragePerUnit  (back-calculate $/SF or $/LF)
   *   normal items   → rate = unitPrice  (already $/PC)
   *
   * Coverage is NOT seeded — it will be computed live from size each render.
   */
  const seedLineEdits = (items) => {
    const edits = {};
    items.forEach((i) => {
      const isCov = isCoverageBased(i.uom);
      const coverage = parseFloat(i.coveragePerUnit) || 1;
      const rate =
        isCov && coverage > 0
          ? (i.unitPrice || 0) / coverage
          : i.unitPrice || 0;
      edits[i._id] = {
        size: i.size || "",
        rate,
        qty: i.quantity || 0,
      };
    });
    setLineEdits(edits);
  };

  const getLE = (id, field, fallback) => lineEdits[id]?.[field] ?? fallback;
  const setLE = (id, field, value) =>
    setLineEdits((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));

  // ── derived ────────────────────────────────────────────────────────────────
  const canDeliver = ["confirmed", "partial"].includes(order?.status);
  const isEditable = ["confirmed", "partial"].includes(order?.status);
  const canAddItems = !["delivered", "void"].includes(order?.status);
  const canVoid = ["confirmed", "partial"].includes(order?.status);
  const vendorId = order?.vendor?._id || order?.vendor;

  const contactOptions = useMemo(
    () =>
      contacts
        .filter(
          (c) =>
            c.relatedAccount === vendorId || selectedContacts.includes(c._id),
        )
        .map((c) => ({
          value: c._id,
          label: `${c.firstName} ${c.lastName}${c.email ? ` (${c.email})` : ""}`,
        })),
    [contacts, vendorId, selectedContacts],
  );

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u._id,
        label: `${u.displayName || "User"}${u.phoneNumber ? ` (${u.phoneNumber})` : ""}`,
      })),
    [users],
  );

  const totals = useMemo(() => {
    const items = (order?.items || []).reduce((s, i) => s + (i.total || 0), 0);
    const surchargesT = surcharges.reduce(
      (s, sc) => s + safeNum(sc.price || sc.amount, 0),
      0,
    );
    return { items, surcharges: surchargesT, total: items + surchargesT };
  }, [order, surcharges]);

  // MO's own files + the linked bill's sales orders / packing slips, in one list
  const allDocs = useMemo(() => {
    const billDocs = (linkedBill?.attachments || []).filter(
      (a) => a.type === "sales_order" || a.type === "packing_slip",
    );
    return [...(order?.attachments || []), ...billDocs];
  }, [order, linkedBill]);

  const liveOrderRows = useMemo(() => {
    return (order?.items || []).map((item) => {
      const isCov = isCoverageBased(item.uom);
      const storedCov = parseFloat(item.coveragePerUnit) || 1;
      const editSize = getLE(item._id, "size", item.size || "");
      const editRate = getLE(
        item._id,
        "rate",
        isCov ? (item.unitPrice || 0) / storedCov : item.unitPrice || 0,
      );
      const editQty =
        parseFloat(getLE(item._id, "qty", item.quantity || 0)) || 0;
      let cov = storedCov;
      if (isCov) {
        const p = parseSizeCoverage(editSize, item.uom);
        if (p !== null) cov = p;
      }
      const perPc = isCov
        ? calcPackageCost(editRate, cov)
        : parseFloat(editRate) || 0;
      return {
        itemCode: item.itemCode,
        size: editSize,
        uom: item.uom,
        quantity: editQty,
        unitPrice: perPc,
        description: item.description || "", // ← add this
        coveragePerUnit: cov,
      };
    });
  }, [order, lineEdits]);
  // Single source of truth for "has this row changed since last save?"
  const isItemDirty = useCallback(
    (item) => {
      const isCov = isCoverageBased(item.uom);
      const storedCov = parseFloat(item.coveragePerUnit) || 1;
      const storedRate = isCov
        ? (item.unitPrice || 0) / storedCov
        : item.unitPrice || 0;
      const e = lineEdits[item._id] || {};
      const editSize = String(e.size ?? item.size ?? "").trim();
      const editQty = parseFloat(e.qty ?? item.quantity ?? 0) || 0;
      const editRate = parseFloat(e.rate ?? storedRate) || 0;
      return (
        editSize !== (item.size || "") ||
        editQty !== (item.quantity || 0) ||
        Math.abs(editRate - storedRate) > 0.0001
      );
    },
    [lineEdits],
  );

  const dirtyCount = useMemo(
    () => (order?.items || []).filter(isItemDirty).length,
    [order, isItemDirty],
  );
  // ── add single item — modal stays open ────────────────────────────────────
  const handleAddSingleItem = async ({
    item,
    qty,
    size,
    coveragePerUnit,
    unitPrice,
  }) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/materialOrders3/${orderId}/add-items`,
        {
          items: [
            {
              itemDatabaseId: item._id,
              quantity: qty,
              unitPrice,
              size,
              coveragePerUnit,
            },
          ],
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updated = res.data.order;
      setOrder(updated);
      setDeliveryQtys((prev) => {
        const next = { ...prev };
        (updated.items || []).forEach((i) => {
          if (!(i._id in next)) next[i._id] = i.qtyPending || 0;
        });
        return next;
      });
      seedLineEdits(updated.items || []);
      // Do NOT close modal here — it manages its own lifecycle
    } catch (err) {
      showAlert(
        "error",
        "Add Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setSaving(false);
    }
  };

  // ── save a single line item ────────────────────────────────────────────────
  /**
   * Resolves coverage from the current size string (same logic as the modal).
   * Falls back to the stored coveragePerUnit if the size can't be parsed.
   * Sends unitPrice as per-PC = rate × coverage to the backend.
   */
  const handleSaveLineItem = async (item) => {
    setSavingLine(item._id);
    try {
      const isCov = isCoverageBased(item.uom);
      const editSize = String(getLE(item._id, "size", item.size || "")).trim();
      const editRate = parseFloat(getLE(item._id, "rate", 0)) || 0;
      const editQty =
        parseFloat(getLE(item._id, "qty", item.quantity || 0)) || 0;

      // Resolve coverage from size (read-only on screen, computed here for sending)
      let coverage = parseFloat(item.coveragePerUnit) || 1;
      if (isCov) {
        const parsed = parseSizeCoverage(editSize, item.uom);
        if (parsed !== null) coverage = parsed;
      }

      const unitPricePerPc = isCov
        ? calcPackageCost(editRate, coverage)
        : editRate;

      const token = localStorage.getItem("token");
      const res = await axios.patch(
        `${API}/materialOrders3/${orderId}/items/${item._id}`,
        {
          size: editSize,
          quantity: editQty,
          unitPrice: unitPricePerPc,
          coveragePerUnit: isCov ? coverage : 0,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updated = res.data.order;
      setOrder(updated);
      seedLineEdits(updated.items || []);
    } catch (err) {
      showAlert(
        "error",
        "Save Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setSavingLine(null);
    }
  };

  // ── order-level save ───────────────────────────────────────────────────────
  // Flushes ALL dirty line items first (each via PATCH), then saves the order
  // logistics in one PUT.  This way the user only needs to click one button.
  // ── order-level save ───────────────────────────────────────────────────────
  // One bulk PATCH for every dirty line item, then one PUT for logistics.
  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const h = { Authorization: `Bearer ${token}` };

      // 1. Collect every dirty line item into ONE payload
      const itemUpdates = (order?.items || [])
        .filter(isItemDirty)
        .map((item) => {
          const isCov = isCoverageBased(item.uom);
          const editSize = String(
            lineEdits[item._id]?.size ?? item.size ?? "",
          ).trim();
          const storedCov = parseFloat(item.coveragePerUnit) || 1;
          const storedRate = isCov
            ? (item.unitPrice || 0) / storedCov
            : item.unitPrice || 0;
          const editRate =
            parseFloat(lineEdits[item._id]?.rate ?? storedRate) || 0;
          const editQty =
            parseFloat(lineEdits[item._id]?.qty ?? item.quantity ?? 0) || 0;

          let coverage = storedCov;
          if (isCov) {
            const parsed = parseSizeCoverage(editSize, item.uom);
            if (parsed !== null) coverage = parsed;
          }
          const unitPricePerPc = isCov
            ? calcPackageCost(editRate, coverage)
            : editRate;

          return {
            itemId: item._id,
            size: editSize,
            quantity: editQty,
            unitPrice: unitPricePerPc,
            coveragePerUnit: isCov ? coverage : 0,
          };
        });

      // 2. One bulk PATCH for all line items (single backend transaction)
      if (itemUpdates.length > 0) {
        const res = await axios.patch(
          `${API}/materialOrders3/${orderId}/items`,
          { items: itemUpdates },
          { headers: h },
        );
        const updated = res.data.order;
        setOrder(updated);
        seedLineEdits(updated.items || []);
      }

      // 3. Save logistics / surcharges via PUT
      await axios.put(
        `${API}/materialOrders3/${orderId}`,
        {
          revision,
          shippingMethod,
          deliveryInstructions,
          expectedDeliveryDate: expectedDeliveryDate || null,
          contact: selectedContacts,
          fieldContact: fieldContacts,
          deliveryContact,
          surcharges,
          description,
        },
        { headers: h },
      );

      showAlert("success", "Saved", "Changes saved successfully.");
    } catch (err) {
      showAlert(
        "error",
        "Save Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Generate ONLY the site PDF and return the blob (no download, no save) ───
  // Mirrors the "site" branch of generateSinglePDF, but returns the blob instead
  // of calling saveAs. Used to attach the site PDF to the site-update email.
  const generateSitePdfBlob = useCallback(async () => {
    if (!order || !project) return null;

    const templateRes = await fetch("/material_order_template2_site.docx");
    if (!templateRes.ok) throw new Error("Could not load site template");
    const templateBuffer = await templateRes.arrayBuffer();

    const base64Regex = /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
    const validBase64 =
      /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
    const base64Parser = (tagValue) => {
      if (typeof tagValue !== "string" || !base64Regex.test(tagValue))
        return null;
      const s = tagValue.replace(base64Regex, "");
      if (!validBase64.test(s)) throw new Error("Invalid base64");
      const bin = window.atob(s);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return bytes.buffer;
    };

    let imageBase64 = null;
    if (companyProfile?.logo) {
      if (companyProfile.logo.startsWith("data:image")) {
        imageBase64 = companyProfile.logo;
      } else {
        try {
          const logoRes = await fetch(companyProfile.logo);
          if (logoRes.ok) {
            const blob = await logoRes.blob();
            imageBase64 = await new Promise((res) => {
              const r = new FileReader();
              r.onloadend = () => res(r.result);
              r.readAsDataURL(blob);
            });
          }
        } catch {}
      }
    }

    const vendor =
      vendors.find((v) => String(v._id) === String(vendorId)) || {};
    const contactsOnOrder = contacts.filter((c) =>
      selectedContacts.map(String).includes(String(c._id)),
    );
    const vendorContactsText =
      contactsOnOrder
        .map(
          (c) =>
            `${c.firstName} ${c.lastName}${c.email ? ` (${c.email})` : ""}`,
        )
        .join(", ") || "N/A";
    const revisionLabel = ["delivered", "void"].includes(order.status)
      ? "FINAL"
      : revision > 0
        ? `R${revision}`
        : "";
    const projectLocation = project.address
      ? [
          project.address.street,
          project.address.city,
          `${project.address.state} ${project.address.postalCode}`,
        ]
          .filter(Boolean)
          .join(", ")
      : project.location || "N/A";

    const fieldContactNames = users
      .filter((u) => fieldContacts.map(String).includes(String(u._id)))
      .map((u) => {
        const name = u.displayName || u.email || "User";
        return u.phoneNumber ? `${name} (${u.phoneNumber})` : name;
      })
      .join(", ");

    const deliveryContactUser = users.find(
      (u) => String(u._id) === String(deliveryContact),
    );
    const deliveryContactName = deliveryContactUser
      ? deliveryContactUser.displayName || deliveryContactUser.email || "User"
      : "N/A";

    const sharedData = {
      companyName: companyProfile?.companyName || "Your Company",
      companyAddress: companyProfile?.address
        ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}`
        : "N/A",
      companyPhone: companyProfile?.phone || "N/A",
      projectName: project.projectName || "Project",
      projectLocation,
      vendorName: vendor.companyName || "Vendor",
      vendorAddress: vendor.address
        ? `${vendor.address.street}, ${vendor.address.city}, ${vendor.address.state} ${vendor.address.postalCode}`
        : "N/A",
      vendorContacts: vendorContactsText,
      materialOrderNumber: order.orderNumber || "MO#",
      poNumber: String(project.projectNumber || ""),
      createdAt: order.createdAt
        ? new Date(order.createdAt).toLocaleDateString("en-US", {
            timeZone: "UTC",
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : "N/A",
      expectedDeliveryDate: expectedDeliveryDate
        ? new Date(expectedDeliveryDate).toLocaleDateString("en-US", {
            timeZone: "UTC",
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : "N/A",
      shippingMethod,
      deliveryInstructions: deliveryInstructions || "N/A",
      revision: revisionLabel,
      image: imageBase64,
      fieldContacts: fieldContactNames || "N/A",
      deliveryContact: deliveryContactName,
    };

    const items = (order.items || [])
      .filter((i) => (i.quantity || 0) > 0)
      .map((i) => {
        const isCov = isCoverageBased(i.uom);
        return {
          description: `${i.description || ""}`.trim(),
          size: i.size || "—",
          uom: isCov ? "PC" : i.uom || "—",
          quantity: String(i.quantity || 0),
          unitPrice: "",
          lineTotal: "",
        };
      });

    const zip = new PizZip(templateBuffer);
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      modules: [
        new ImageModule({ getImage: base64Parser, getSize: () => [125, 90] }),
      ],
    });
    doc.setData({
      ...sharedData,
      items,
      subtotal: "",
      taxRate: "",
      taxAmount: "",
      grandTotal: "",
    });
    doc.render();

    const docxBlob = doc.getZip().generate({ type: "blob" });
    const formData = new FormData();
    formData.append("docx", docxBlob, `MO_${order.orderNumber}_SITE.docx`);
    formData.append("documentType", "MaterialOrder");
    formData.append("identifier", `${order.orderNumber}_SITE_UPDATE`);

    const token = localStorage.getItem("token");
    const pdfRes = await axios.post(`${API}/upload/convert-to-pdf`, formData, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });
    if (!pdfRes.headers["content-type"]?.includes("application/pdf"))
      throw new Error(`PDF conversion failed: ${await pdfRes.data.text()}`);

    return new Blob([pdfRes.data], { type: "application/pdf" });
  }, [
    order,
    project,
    companyProfile,
    vendors,
    contacts,
    selectedContacts,
    fieldContacts,
    deliveryContact,
    users,
    revision,
    shippingMethod,
    deliveryInstructions,
    expectedDeliveryDate,
    vendorId,
  ]);

  // ── Open the site-update modal: build the PDF, seed subject, show form ─────
  const handleOpenSiteUpdate = async () => {
    setSaving(true);
    try {
      const blob = await generateSitePdfBlob();
      setSiteUpdateBuffer(blob);
      setSiteUpdateForm({
        subject: `Update: MO# ${project.projectNumber}-${order.orderNumber}`,
        message: "",
      });
      setShowSiteUpdateForm(true);
    } catch (err) {
      showAlert("error", "PDF Failed", err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Submit the site update email ────────────────────────────────────────────
  const handleSendSiteUpdate = async () => {
    if (!siteUpdateForm.message.trim()) {
      showAlert("error", "Missing Message", "Enter an update message to send.");
      return;
    }
    if (!(siteUpdateBuffer instanceof Blob)) {
      showAlert("error", "Missing PDF", "PDF not ready. Try again.");
      return;
    }
    setSendingSiteUpdate(true);
    try {
      const token = localStorage.getItem("token");
      const safeProjectName = (project?.projectName || "Project")
        .replace(/[^a-zA-Z0-9-_ ]/g, "")
        .trim();
      const formData = new FormData();
      formData.append("from", userEmail);
      formData.append("subject", siteUpdateForm.subject);
      formData.append("message", siteUpdateForm.message);
      formData.append(
        "siteFile",
        siteUpdateBuffer,
        `MaterialOrder-${order.orderNumber} - ${safeProjectName} - SITE.pdf`,
      );

      const res = await axios.post(
        `${API}/materialOrders3/${orderId}/send-site-update`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setShowSiteUpdateForm(false);
      setSiteUpdateBuffer(null);
      showAlert(
        "success",
        "Update Sent",
        `Site update sent to ${res.data.recipients?.length || 0} recipient(s).`,
      );
    } catch (err) {
      showAlert(
        "error",
        "Send Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setSendingSiteUpdate(false);
    }
  };

  // ── deliver ────────────────────────────────────────────────────────────────
  const handleDeliver = async () => {
    setDelivering(true);
    try {
      const token = localStorage.getItem("token");
      const items = (order.items || [])
        .map((i) => ({
          itemId: i._id,
          qtyDelivered: parseFloat(deliveryQtys[i._id]) || 0,
          varianceReason: varianceReasons[i._id] || undefined,
        }))
        .filter((i) => i.qtyDelivered > 0);
      if (!items.length) {
        showAlert(
          "error",
          "No Quantities",
          "Enter at least one delivered quantity.",
        );
        setDelivering(false);
        return;
      }
      const res = await axios.post(
        `${API}/materialOrders3/${orderId}/deliver`,
        { items },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updated = res.data.order;
      setOrder(updated);
      setDeliveryQtys(
        Object.fromEntries(
          (updated.items || []).map((i) => [i._id, i.qtyPending || 0]),
        ),
      );
      setVarianceReasons({});
      seedLineEdits(updated.items || []);
      showAlert("success", "Delivered", "Delivery recorded successfully.");
    } catch (err) {
      showAlert(
        "error",
        "Delivery Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setDelivering(false);
    }
  };

  // ── void ───────────────────────────────────────────────────────────────────
  const handleVoid = () => {
    showAlert(
      "warning",
      "Confirm Void",
      "Void this order? All pending quantities will be released.",
      {
        confirmText: "Void Order",
        cancelText: "Cancel",
        onConfirm: async () => {
          setAlert((p) => ({ ...p, open: false }));
          setSaving(true);
          try {
            const token = localStorage.getItem("token");
            const res = await axios.post(
              `${API}/materialOrders3/${orderId}/void`,
              { voidReason: "Voided by user" },
              { headers: { Authorization: `Bearer ${token}` } },
            );
            setOrder(res.data.order);
            showAlert("success", "Voided", "Order voided successfully.", {
              onConfirm: goBack,
            });
          } catch (err) {
            showAlert(
              "error",
              "Error",
              err.response?.data?.message || err.message,
            );
          } finally {
            setSaving(false);
          }
        },
      },
    );
  };

  const handleReactivate = () => {
    showAlert(
      "warning",
      "Reactivate Order",
      "Reactivate this voided order? It will return to an editable state.",
      {
        confirmText: "Reactivate",
        cancelText: "Cancel",
        onConfirm: async () => {
          setAlert((p) => ({ ...p, open: false }));
          setSaving(true);
          try {
            const token = localStorage.getItem("token");
            const res = await axios.post(
              `${API}/materialOrders3/${orderId}/reactivate`,
              {},
              { headers: { Authorization: `Bearer ${token}` } },
            );
            const updated = res.data.order;
            setOrder(updated);
            setDeliveryQtys(
              Object.fromEntries(
                (updated.items || []).map((i) => [i._id, i.qtyPending || 0]),
              ),
            );
            seedLineEdits(updated.items || []);
            showAlert(
              "success",
              "Reactivated",
              "Order reactivated successfully.",
            );
          } catch (err) {
            showAlert(
              "error",
              "Reactivate Failed",
              err.response?.data?.message || err.message,
            );
          } finally {
            setSaving(false);
          }
        },
      },
    );
  };

  // ── remove item ────────────────────────────────────────────────────────────
  const handleRemoveItem = (itemId) => {
    showAlert(
      "warning",
      "Remove Item",
      "Remove this item? Pending quantities will be reversed.",
      {
        confirmText: "Remove",
        cancelText: "Cancel",
        onConfirm: async () => {
          setAlert((p) => ({ ...p, open: false }));
          setSaving(true);
          try {
            const token = localStorage.getItem("token");
            const res = await axios.delete(
              `${API}/materialOrders3/${orderId}/items/${itemId}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            const updated = res.data.order;
            setOrder(updated);
            setDeliveryQtys(
              Object.fromEntries(
                (updated.items || []).map((i) => [i._id, i.qtyPending || 0]),
              ),
            );
            seedLineEdits(updated.items || []);
          } catch (err) {
            showAlert(
              "error",
              "Remove Failed",
              err.response?.data?.message || err.message,
            );
          } finally {
            setSaving(false);
          }
        },
      },
    );
  };

  // ── revert delivery ──────────────────────────────────────────────────────────
  const handleRevertDelivery = () => {
    showAlert(
      "warning",
      "Revert Delivery",
      "Reset all delivered quantities back to pending? This re-opens the order so you can edit pricing or quantities, and reverses delivered actuals on the budget.",
      {
        confirmText: "Revert to Pending",
        cancelText: "Cancel",
        onConfirm: async () => {
          setAlert((p) => ({ ...p, open: false }));
          setSaving(true);
          try {
            const token = localStorage.getItem("token");
            const res = await axios.post(
              `${API}/materialOrders3/${orderId}/revert-delivery`,
              {}, // empty body = full undo
              { headers: { Authorization: `Bearer ${token}` } },
            );
            const updated = res.data.order;
            setOrder(updated);
            setDeliveryQtys(
              Object.fromEntries(
                (updated.items || []).map((i) => [i._id, i.qtyPending || 0]),
              ),
            );
            setVarianceReasons({});
            seedLineEdits(updated.items || []);
            showAlert(
              "success",
              "Reverted",
              "Deliveries reset. Order re-opened for editing.",
            );
          } catch (err) {
            showAlert(
              "error",
              "Revert Failed",
              err.response?.data?.message || err.message,
            );
          } finally {
            setSaving(false);
          }
        },
      },
    );
  };

  // Add this helper above generatePDF
  const saveLogistics = useCallback(async () => {
    const token = localStorage.getItem("token");
    await axios.put(
      `${API}/materialOrders3/${orderId}`,
      {
        revision,
        shippingMethod,
        deliveryInstructions,
        expectedDeliveryDate: expectedDeliveryDate || null,
        contact: selectedContacts,
        fieldContact: fieldContacts,
        deliveryContact,
        surcharges,
        description, // ADD
      },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  }, [
    orderId,
    revision,
    shippingMethod,
    deliveryInstructions,
    expectedDeliveryDate,
    selectedContacts,
    fieldContacts,
    deliveryContact,
    surcharges,
    description, // ADD to deps
  ]);

  // ── PDF ────────────────────────────────────────────────────────────────────
  const generatePDF = useCallback(
    async (forEmail = false) => {
      if (!order || !project) return;
      setSaving(true);
      try {
        // Load both templates in parallel
        const [vendorTemplateRes, siteTemplateRes] = await Promise.all([
          fetch("/material_order_template2_vendor.docx"),
          fetch("/material_order_template2_site.docx"),
        ]);
        if (!vendorTemplateRes.ok)
          throw new Error("Could not load vendor PDF template");
        if (!siteTemplateRes.ok)
          throw new Error("Could not load site PDF template");

        const vendorTemplateBuffer = await vendorTemplateRes.arrayBuffer();
        const siteTemplateBuffer = await siteTemplateRes.arrayBuffer();

        const base64Regex =
          /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
        const validBase64 =
          /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
        const base64Parser = (tagValue) => {
          if (typeof tagValue !== "string" || !base64Regex.test(tagValue))
            return null;
          const s = tagValue.replace(base64Regex, "");
          if (!validBase64.test(s)) throw new Error("Invalid base64");
          const bin = window.atob(s);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          return bytes.buffer;
        };

        let imageBase64 = null;
        if (companyProfile?.logo) {
          if (companyProfile.logo.startsWith("data:image")) {
            imageBase64 = companyProfile.logo;
          } else {
            try {
              const logoRes = await fetch(companyProfile.logo);
              if (logoRes.ok) {
                const blob = await logoRes.blob();
                imageBase64 = await new Promise((res) => {
                  const r = new FileReader();
                  r.onloadend = () => res(r.result);
                  r.readAsDataURL(blob);
                });
              }
            } catch {}
          }
        }

        const vendor =
          vendors.find((v) => String(v._id) === String(vendorId)) || {};
        const contactsOnOrder = contacts.filter((c) =>
          selectedContacts.map(String).includes(String(c._id)),
        );
        const vendorContactsText =
          contactsOnOrder
            .map(
              (c) =>
                `${c.firstName} ${c.lastName}${c.email ? ` (${c.email})` : ""}`,
            )
            .join(", ") || "N/A";
        const revisionLabel = ["delivered", "void"].includes(order.status)
          ? "FINAL"
          : revision > 0
            ? `R${revision}`
            : "";
        const projectLocation = project.address
          ? [
              project.address.street,
              project.address.city,
              `${project.address.state} ${project.address.postalCode}`,
            ]
              .filter(Boolean)
              .join(", ")
          : project.location || "N/A";

        const templateItems = (order.items || [])
          .filter((i) => (i.quantity || 0) > 0)
          .map((i) => {
            const isCov = isCoverageBased(i.uom);
            const cov = i.coveragePerUnit || 1;
            const rate =
              isCov && cov > 0 ? (i.unitPrice || 0) / cov : i.unitPrice || 0;
            return {
              description: `${i.description || ""}`.trim(),
              size: i.size || "—",
              uom: isCov ? `${cov} ${i.uom}/PC` : i.uom || "—",
              quantity: String(i.quantity || 0),
              up: `$${fmtRate(rate)}`,
              lineTotal: `$${fmt(i.total || 0)}`,
            };
          });
        surcharges.forEach((s) => {
          if (safeNum(s.price || s.amount) !== 0) {
            templateItems.push({
              description: s.description || "Surcharge",
              size: "—",
              uom: "—",
              quantity: "1",
              up: `$${fmt(safeNum(s.price || s.amount))}`,
              lineTotal: `$${fmt(safeNum(s.price || s.amount))}`,
            });
          }
        });

        // Site items — no pricing columns
        const siteItems = (order.items || [])
          .filter((i) => (i.quantity || 0) > 0)
          .map((i) => {
            const isCov = isCoverageBased(i.uom);
            return {
              description: `${i.description || ""}`.trim(),
              size: i.size || "—",
              uom: isCov ? "PC" : i.uom || "—",
              quantity: String(i.quantity || 0),
              unitPrice: "",
              lineTotal: "",
            };
          });

        const fieldContactNames = users
          .filter((u) => fieldContacts.map(String).includes(String(u._id)))
          .map((u) => {
            const name = u.displayName || u.email || "User";
            return u.phoneNumber ? `${name} (${u.phoneNumber})` : name;
          })
          .join(", ");

        const deliveryContactUser = users.find(
          (u) => String(u._id) === String(deliveryContact),
        );
        const deliveryContactName = deliveryContactUser
          ? deliveryContactUser.displayName ||
            deliveryContactUser.email ||
            "User"
          : "N/A";

        const subtotal = totals.total;
        const taxRate = parseFloat(companyProfile?.defaultTax?.taxRate || 0);
        const tax = subtotal * (taxRate / 100);
        const grandTotal = subtotal + tax;

        // Shared fields (both PDFs)
        const sharedData = {
          companyName: companyProfile?.companyName || "Your Company",
          companyAddress: companyProfile?.address
            ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}`
            : "N/A",
          companyPhone: companyProfile?.phone || "N/A",
          projectName: project.projectName || "Project",
          projectLocation,
          vendorName: vendor.companyName || "Vendor",
          vendorAddress: vendor.address
            ? `${vendor.address.street}, ${vendor.address.city}, ${vendor.address.state} ${vendor.address.postalCode}`
            : "N/A",
          vendorContacts: vendorContactsText,
          materialOrderNumber: order.orderNumber || "MO#",
          poNumber: String(project.projectNumber || ""),
          createdAt: order.createdAt
            ? new Date(order.createdAt).toLocaleDateString("en-US", {
                timeZone: "UTC",
                year: "numeric",
                month: "long",
                day: "numeric",
              })
            : "N/A",
          expectedDeliveryDate: expectedDeliveryDate
            ? new Date(expectedDeliveryDate).toLocaleDateString("en-US", {
                timeZone: "UTC",
                year: "numeric",
                month: "long",
                day: "numeric",
              })
            : "N/A",
          shippingMethod,
          deliveryInstructions: deliveryInstructions || "N/A",
          revision: revisionLabel,
          image: imageBase64,
          fieldContacts: fieldContactNames || "N/A",
          deliveryContact: deliveryContactName,
        };

        // Helper: render one docxtemplater doc → PDF blob
        const renderToPdf = async (templateBuffer, data, label) => {
          const zip = new PizZip(templateBuffer);
          const doc = new Docxtemplater(zip, {
            paragraphLoop: true,
            linebreaks: true,
            modules: [
              new ImageModule({
                getImage: base64Parser,
                getSize: () => [125, 90],
              }),
            ],
          });
          doc.setData(data);
          doc.render();

          const docxBlob = doc.getZip().generate({ type: "blob" });
          const formData = new FormData();
          formData.append(
            "docx",
            docxBlob,
            `MO_${order.orderNumber}_${label}.docx`,
          );
          formData.append("documentType", "MaterialOrder");
          formData.append("identifier", `${order.orderNumber}_${label}`);

          const token = localStorage.getItem("token");
          const pdfRes = await axios.post(
            `${API}/upload/convert-to-pdf`,
            formData,
            {
              headers: { Authorization: `Bearer ${token}` },
              responseType: "blob",
            },
          );
          if (!pdfRes.headers["content-type"]?.includes("application/pdf"))
            throw new Error(
              `PDF conversion failed (${label}): ${await pdfRes.data.text()}`,
            );
          return new Blob([pdfRes.data], { type: "application/pdf" });
        };

        const vendorPdf = await renderToPdf(
          vendorTemplateBuffer,
          {
            ...sharedData,
            items: templateItems,
            subtotal: `$${fmt(subtotal)}`,
            taxRate: taxRate > 0 ? `${taxRate}%` : "0%",
            taxAmount: `$${fmt(tax)}`,
            grandTotal: `$${fmt(grandTotal)}`,
          },
          "VENDOR",
        );

        const sitePdf = await renderToPdf(
          siteTemplateBuffer,
          {
            ...sharedData,
            items: siteItems,
            subtotal: "",
            taxRate: "",
            taxAmount: "",
            grandTotal: "",
          },
          "SITE",
        );

        if (forEmail) {
          await saveLogistics();
          setVendorPdfBuffer(vendorPdf);
          setSitePdfBuffer(sitePdf);
          setShowEmailForm(true);
        } else {
          const fileDate = new Date().toISOString().split("T")[0];
          const fileBase = `${fileDate}-${project.projectNumber}-MO${order.orderNumber}`;
          saveAs(vendorPdf, `${fileBase}.pdf`);
          setTimeout(() => saveAs(sitePdf, `${fileBase}-SITE.pdf`), 500);
          await saveLogistics();
          showAlert(
            "success",
            "Downloaded",
            "Vendor PDF and site PDF downloaded successfully.",
          );
        }
      } catch (err) {
        showAlert("error", "PDF Failed", err.message);
      } finally {
        setSaving(false);
      }
    },
    [
      order,
      project,
      companyProfile,
      vendors,
      contacts,
      selectedContacts,
      fieldContacts,
      deliveryContact,
      users,
      surcharges,
      revision,
      shippingMethod,
      deliveryInstructions,
      expectedDeliveryDate,
      totals,
      vendorId,
      saveLogistics,
    ],
  );

  const generateSinglePDF = useCallback(
    async (mode) => {
      // mode: "vendor" | "site"
      if (!order || !project) return;
      setSaving(true);
      try {
        const templateFile =
          mode === "vendor"
            ? "/material_order_template2_vendor.docx"
            : "/material_order_template2_site.docx";

        const templateRes = await fetch(templateFile);
        if (!templateRes.ok) throw new Error(`Could not load ${mode} template`);
        const templateBuffer = await templateRes.arrayBuffer();

        const base64Regex =
          /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
        const validBase64 =
          /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
        const base64Parser = (tagValue) => {
          if (typeof tagValue !== "string" || !base64Regex.test(tagValue))
            return null;
          const s = tagValue.replace(base64Regex, "");
          if (!validBase64.test(s)) throw new Error("Invalid base64");
          const bin = window.atob(s);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          return bytes.buffer;
        };

        let imageBase64 = null;
        if (companyProfile?.logo) {
          if (companyProfile.logo.startsWith("data:image")) {
            imageBase64 = companyProfile.logo;
          } else {
            try {
              const logoRes = await fetch(companyProfile.logo);
              if (logoRes.ok) {
                const blob = await logoRes.blob();
                imageBase64 = await new Promise((res) => {
                  const r = new FileReader();
                  r.onloadend = () => res(r.result);
                  r.readAsDataURL(blob);
                });
              }
            } catch {}
          }
        }

        const vendor =
          vendors.find((v) => String(v._id) === String(vendorId)) || {};
        const contactsOnOrder = contacts.filter((c) =>
          selectedContacts.map(String).includes(String(c._id)),
        );
        const vendorContactsText =
          contactsOnOrder
            .map(
              (c) =>
                `${c.firstName} ${c.lastName}${c.email ? ` (${c.email})` : ""}`,
            )
            .join(", ") || "N/A";
        const revisionLabel = ["delivered", "void"].includes(order.status)
          ? "FINAL"
          : revision > 0
            ? `R${revision}`
            : "";
        const projectLocation = project.address
          ? [
              project.address.street,
              project.address.city,
              `${project.address.state} ${project.address.postalCode}`,
            ]
              .filter(Boolean)
              .join(", ")
          : project.location || "N/A";

        const fieldContactNames = users
          .filter((u) => fieldContacts.map(String).includes(String(u._id)))
          .map((u) => {
            const name = u.displayName || u.email || "User";
            return u.phoneNumber ? `${name} (${u.phoneNumber})` : name;
          })
          .join(", ");

        const deliveryContactUser = users.find(
          (u) => String(u._id) === String(deliveryContact),
        );
        const deliveryContactName = deliveryContactUser
          ? deliveryContactUser.displayName ||
            deliveryContactUser.email ||
            "User"
          : "N/A";

        const subtotal = totals.total;
        const taxRate = parseFloat(companyProfile?.defaultTax?.taxRate || 0);
        const tax = subtotal * (taxRate / 100);
        const grandTotal = subtotal + tax;

        const sharedData = {
          companyName: companyProfile?.companyName || "Your Company",
          companyAddress: companyProfile?.address
            ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}`
            : "N/A",
          companyPhone: companyProfile?.phone || "N/A",
          projectName: project.projectName || "Project",
          projectLocation,
          vendorName: vendor.companyName || "Vendor",
          vendorAddress: vendor.address
            ? `${vendor.address.street}, ${vendor.address.city}, ${vendor.address.state} ${vendor.address.postalCode}`
            : "N/A",
          vendorContacts: vendorContactsText,
          materialOrderNumber: order.orderNumber || "MO#",
          poNumber: String(project.projectNumber || ""),
          createdAt: order.createdAt
            ? new Date(order.createdAt).toLocaleDateString("en-US", {
                timeZone: "UTC",
                year: "numeric",
                month: "long",
                day: "numeric",
              })
            : "N/A",
          expectedDeliveryDate: expectedDeliveryDate
            ? new Date(expectedDeliveryDate).toLocaleDateString("en-US", {
                timeZone: "UTC",
                year: "numeric",
                month: "long",
                day: "numeric",
              })
            : "N/A",
          shippingMethod,
          deliveryInstructions: deliveryInstructions || "N/A",
          revision: revisionLabel,
          image: imageBase64,
          fieldContacts: fieldContactNames || "N/A",
          deliveryContact: deliveryContactName,
        };

        let items;
        if (mode === "vendor") {
          const templateItems = (order.items || [])
            .filter((i) => (i.quantity || 0) > 0)
            .map((i) => {
              const isCov = isCoverageBased(i.uom);
              const cov = i.coveragePerUnit || 1;
              const rate =
                isCov && cov > 0 ? (i.unitPrice || 0) / cov : i.unitPrice || 0;
              return {
                description: `${i.description || ""}`.trim(),
                size: i.size || "—",
                uom: isCov ? `${cov} ${i.uom}/PC` : i.uom || "—",
                quantity: String(i.quantity || 0),
                up: `$${fmtRate(rate)}`,
                lineTotal: `$${fmt(i.total || 0)}`,
              };
            });
          surcharges.forEach((s) => {
            if (safeNum(s.price || s.amount) !== 0) {
              templateItems.push({
                description: s.description || "Surcharge",
                size: "—",
                uom: "—",
                quantity: "1",
                up: `$${fmt(safeNum(s.price || s.amount))}`,
                lineTotal: `$${fmt(safeNum(s.price || s.amount))}`,
              });
            }
          });
          items = templateItems;
        } else {
          items = (order.items || [])
            .filter((i) => (i.quantity || 0) > 0)
            .map((i) => {
              const isCov = isCoverageBased(i.uom);
              return {
                description: `${i.description || ""}`.trim(),
                size: i.size || "—",
                uom: isCov ? "PC" : i.uom || "—",
                quantity: String(i.quantity || 0),
                unitPrice: "",
                lineTotal: "",
              };
            });
        }

        const zip = new PizZip(templateBuffer);
        const doc = new Docxtemplater(zip, {
          paragraphLoop: true,
          linebreaks: true,
          modules: [
            new ImageModule({
              getImage: base64Parser,
              getSize: () => [125, 90],
            }),
          ],
        });

        const docData =
          mode === "vendor"
            ? {
                ...sharedData,
                items,
                subtotal: `$${fmt(subtotal)}`,
                taxRate: taxRate > 0 ? `${taxRate}%` : "0%",
                taxAmount: `$${fmt(tax)}`,
                grandTotal: `$${fmt(grandTotal)}`,
              }
            : {
                ...sharedData,
                items,
                subtotal: "",
                taxRate: "",
                taxAmount: "",
                grandTotal: "",
              };

        doc.setData(docData);
        doc.render();

        const docxBlob = doc.getZip().generate({ type: "blob" });
        const formData = new FormData();
        const label = mode === "vendor" ? "VENDOR" : "SITE";
        formData.append(
          "docx",
          docxBlob,
          `MO_${order.orderNumber}_${label}.docx`,
        );
        formData.append("documentType", "MaterialOrder");
        formData.append("identifier", `${order.orderNumber}_${label}`);

        const token = localStorage.getItem("token");
        const pdfRes = await axios.post(
          `${API}/upload/convert-to-pdf`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          },
        );
        if (!pdfRes.headers["content-type"]?.includes("application/pdf"))
          throw new Error(`PDF conversion failed: ${await pdfRes.data.text()}`);

        const pdfBlob = new Blob([pdfRes.data], { type: "application/pdf" });
        const fileDate = new Date().toISOString().split("T")[0];
        const fileBase = `${fileDate}-${project.projectNumber}-MO${order.orderNumber}`;
        const filename =
          mode === "vendor" ? `${fileBase}.pdf` : `${fileBase}-SITE.pdf`;
        saveAs(pdfBlob, filename);

        await saveLogistics();
        showAlert(
          "success",
          "Downloaded",
          `${mode === "vendor" ? "Vendor" : "Site"} PDF downloaded successfully.`,
        );
      } catch (err) {
        showAlert("error", "PDF Failed", err.message);
      } finally {
        setSaving(false);
      }
    },
    [
      order,
      project,
      companyProfile,
      vendors,
      contacts,
      selectedContacts,
      fieldContacts,
      deliveryContact,
      users,
      surcharges,
      revision,
      shippingMethod,
      deliveryInstructions,
      expectedDeliveryDate,
      totals,
      vendorId,
      saveLogistics,
    ],
  );

  const handleSendEmail = () => generatePDF(true);
  const handleEmailSubmit = async () => {
    try {
      if (!emailForm.to || !emailForm.subject) {
        showAlert("error", "Missing Fields", "To and Subject are required.");
        return;
      }
      if (!(vendorPdfBuffer instanceof Blob)) {
        showAlert("error", "Missing PDF", "PDF not ready. Try again.");
        return;
      }
      const token = localStorage.getItem("token");
      const safeProjectName = (project?.projectName || "Project")
        .replace(/[^a-zA-Z0-9-_ ]/g, "")
        .trim();
      const fileDate = new Date().toISOString().split("T")[0];
      const formData = new FormData();
      formData.append("to", emailForm.to);
      formData.append("subject", emailForm.subject);
      formData.append("body", emailForm.body);
      formData.append("signature", emailForm.signature || "");
      formData.append("from", userEmail);
      formData.append(
        "files",
        vendorPdfBuffer,
        `MaterialOrder-${order.orderNumber} - ${safeProjectName}.pdf`,
      );
      if (sitePdfBuffer instanceof Blob) {
        formData.append(
          "siteFiles",
          sitePdfBuffer,
          `MaterialOrder-${order.orderNumber} - ${safeProjectName} - SITE.pdf`,
        );
      }
      await axios.post(
        `${API}/materialOrders3/${orderId}/send-email`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowEmailForm(false);
      setVendorPdfBuffer(null);
      setSitePdfBuffer(null);
      showAlert("success", "Email Sent", "Material order sent successfully.");
    } catch (err) {
      showAlert(
        "error",
        "Email Failed",
        err.response?.data?.message || err.message,
      );
    }
  };

  const addSurcharge = () =>
    setSurcharges((p) => [...p, { description: "", price: 0 }]);
  const updateSurcharge = (i, field, val) =>
    setSurcharges((p) =>
      p.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)),
    );
  const removeSurcharge = (i) =>
    setSurcharges((p) => p.filter((_, idx) => idx !== i));

  const addSuggestedSurcharge = (sugg) => {
    const already = surcharges.some(
      (s) =>
        (s.description || "").toLowerCase() ===
          sugg.description.toLowerCase() &&
        safeNum(s.price ?? s.amount) === sugg.price,
    );
    if (!already)
      setSurcharges((p) => [
        ...p,
        { description: sugg.description, price: sugg.price },
      ]);
  };

  // ── render ─────────────────────────────────────────────────────────────────
  if (loading)
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-800">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  if (!order || !project)
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-800 text-gray-400">
        No data
      </div>
    );

  const statusColor = STATUS_BADGE[order.status] || "bg-gray-700 text-gray-300";
  const revisionLabel = ["delivered", "void"].includes(order.status)
    ? "FINAL"
    : revision > 0
      ? `R${revision}`
      : "";
  const hasCovItems = (order.items || []).some((i) => isCoverageBased(i.uom));

  return (
    <div className="mx-auto px-4 py-6 space-y-6">
      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        confirmText={alert.confirmText}
        cancelText={alert.cancelText}
        onClose={alert.onClose}
        onConfirm={alert.onConfirm}
      />
      {showAddItems && (
        <AddItemsModal3
          orderId={orderId}
          onSelect={handleAddSingleItem}
          onClose={() => setShowAddItems(false)}
          showAlert={showAlert}
        />
      )}
      {/* Header */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={goBack}
            className="px-3 py-1.5 text-sm text-gray-400 hover:text-white bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            ← Back
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-white">
                MO# {project.projectNumber} - {order.orderNumber}
              </h1>
              <span
                className={cls(
                  "px-2 py-0.5 text-[10px] font-semibold rounded-full",
                  statusColor,
                )}
              >
                {order.status?.toUpperCase()}
              </span>
              <span className="text-xs text-gray-500">{revisionLabel}</span>
              {billLoading ? (
                <span className="text-[10px] text-gray-600">
                  checking bill…
                </span>
              ) : linkedBill ? (
                <button
                  type="button"
                  onClick={() => setShowBillReviewPanel(true)}
                  title="Review uploaded bill"
                  className={cls(
                    "inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full cursor-pointer hover:ring-1 hover:ring-orange-400 transition",
                    linkedBill.processingStatus === "processed"
                      ? "bg-emerald-600/70 text-emerald-100"
                      : "bg-yellow-600/70 text-yellow-100",
                  )}
                >
                  Bill{" "}
                  {linkedBill.processingStatus === "processed"
                    ? "processed"
                    : "pending"}{" "}
                  · Review
                </button>
              ) : (
                <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full bg-gray-700/60 text-gray-500">
                  No bill
                </span>
              )}
            </div>
            <p className="text-md text-gray-400 mt-0.5">
              {vendors.find((v) => v._id === vendorId)?.companyName || "Vendor"}{" "}
              · PO #{order.purchaseOrder?.poNumber || "—"} -{" "}
              {order.purchaseOrder?.title}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button
            onClick={() => navigate(`/Projects/${projectId}?tab=Materials`)}
            className="px-3 py-1.5 text-sm text-gray-400 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          {linkedBill && (
            <button
              onClick={() => setShowBillReviewPanel(true)}
              className="px-3 py-1.5 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
            >
              Review Bill
            </button>
          )}

          {canVoid && (
            <button
              onClick={handleVoid}
              disabled={saving}
              className="px-3 py-1.5 text-sm font-medium text-white bg-red-700 rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors"
            >
              Void
            </button>
          )}
          {canReactivate && (
            <button
              onClick={handleReactivate}
              disabled={saving}
              className="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-500 disabled:opacity-50 transition-colors"
            >
              Reactivate
            </button>
          )}
          {canAddItems && (
            <button
              onClick={() => setShowAddItems(true)}
              disabled={saving}
              className="px-3 py-1.5 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-500 disabled:opacity-50 transition-colors"
            >
              + Add Items
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className={cls(
              "px-3 py-1.5 text-sm font-medium text-white rounded-lg disabled:opacity-50 transition-colors",
              dirtyCount > 0
                ? "bg-orange-500 hover:bg-orange-600"
                : "bg-blue-600 hover:bg-blue-700",
            )}
          >
            {saving
              ? "Saving…"
              : isEditable
                ? `Save Changes${dirtyCount ? ` (${dirtyCount})` : ""}`
                : "Save Description & Surcharges"}
          </button>

          <button
            onClick={() => generateSinglePDF("vendor")}
            disabled={saving}
            className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {saving ? "Generating…" : "Vendor PDF"}
          </button>
          <button
            onClick={() => generateSinglePDF("site")}
            disabled={saving}
            className="px-3 py-1.5 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50 transition-colors"
          >
            {saving ? "Generating…" : "Site PDF"}
          </button>
          <button
            onClick={handleSendEmail}
            disabled={saving}
            className="px-3 py-1.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            {saving ? "Preparing…" : "Send Email"}
          </button>
          <button
            onClick={handleOpenSiteUpdate}
            disabled={saving}
            className="px-3 py-1.5 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 disabled:opacity-50 transition-colors"
          >
            {saving ? "Preparing…" : "Send Site Update"}
          </button>
        </div>
      </div>
      {/* Order Details */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-4">
        <h2 className="text-sm font-semibold text-white">Order Details</h2>
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wide mb-1 block">
            Description
          </label>
          {order.status !== "void" ? (
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Drywall + mud for 2nd floor units"
              className="w-full px-3 py-2 text-sm rounded border border-gray-600 bg-gray-700 text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
            />
          ) : (
            <p className="text-sm text-gray-200">{description || "—"}</p>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide mb-1 block">
              Revision
            </label>
            {isEditable ? (
              <NumericFormat
                value={revision}
                onValueChange={(v) => setRevision(v.floatValue || 0)}
                allowNegative={false}
                decimalScale={0}
                className="w-20 px-2 py-1.5 text-sm rounded border border-gray-600 bg-gray-700 text-gray-200 focus:border-orange-500 focus:outline-none"
              />
            ) : (
              <p className="text-sm text-gray-200">{revisionLabel}</p>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide mb-1 block">
              Shipping
            </label>
            {isEditable ? (
              <select
                value={shippingMethod}
                onChange={(e) => setShippingMethod(e.target.value)}
                className="w-full px-2 py-1.5 text-sm rounded border border-gray-600 bg-gray-700 text-gray-200 focus:border-orange-500 focus:outline-none"
              >
                <option>Delivery</option>
                <option>Pickup</option>
              </select>
            ) : (
              <p className="text-sm text-gray-200">{shippingMethod}</p>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide mb-1 block">
              Expected Delivery
            </label>
            {isEditable ? (
              <input
                type="date"
                value={expectedDeliveryDate}
                onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                className="w-full px-2 py-1.5 text-sm rounded border border-gray-600 bg-gray-700 text-gray-200 focus:border-orange-500 focus:outline-none"
              />
            ) : (
              <p className="text-sm text-gray-200">
                {expectedDeliveryDate || "Not set"}
              </p>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide mb-1 block">
              Status
            </label>
            <span
              className={cls(
                "inline-block px-2 py-0.5 text-xs font-semibold rounded-full",
                statusColor,
              )}
            >
              {order.status?.toUpperCase()}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide mb-1 block">
              Vendor Contacts
            </label>
            <Select
              isMulti
              options={contactOptions}
              value={contactOptions.filter((o) =>
                selectedContacts.includes(o.value),
              )}
              onChange={(sel) =>
                setSelectedContacts(sel ? sel.map((o) => o.value) : [])
              }
              styles={selectStyles}
              placeholder="Select contacts…"
              isDisabled={!isEditable}
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide mb-1 block">
              Field Contacts
            </label>
            <Select
              isMulti
              options={userOptions}
              value={userOptions.filter((o) => fieldContacts.includes(o.value))}
              onChange={(sel) =>
                setFieldContacts(sel ? sel.map((o) => o.value) : [])
              }
              styles={selectStyles}
              placeholder="Select users…"
              isDisabled={!isEditable}
            />
          </div>
          {shippingMethod === "Pickup" && (
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wide mb-1 block">
                Delivery Driver
              </label>
              <Select
                options={userOptions}
                value={
                  userOptions.find((o) => o.value === deliveryContact) || null
                }
                onChange={(sel) => setDeliveryContact(sel?.value || null)}
                styles={selectStyles}
                placeholder="Select driver…"
                isClearable
                isDisabled={!isEditable}
              />
            </div>
          )}
        </div>
        {isEditable && (
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide mb-1 block">
              Delivery Instructions
            </label>
            <textarea
              value={deliveryInstructions}
              onChange={(e) => setDeliveryInstructions(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm rounded border border-gray-600 bg-gray-700 text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none resize-none"
              placeholder="Access instructions, floor, loading dock…"
            />
          </div>
        )}
      </div>

      {/* Bill & Documents */}
      {/* Collapsible documents section */}
      <div className="border border-gray-700 rounded-lg overflow-hidden">
        <button
          onClick={() => setShowBillViewer((p) => !p)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-900 hover:bg-gray-850 transition-colors text-left"
        >
          <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
            Documents &amp; Uploads
            {allDocs.length > 0 && (
              <span className="ml-2 text-gray-500 font-normal normal-case">
                ({allDocs.length} file{allDocs.length !== 1 ? "s" : ""}{" "}
                attached)
              </span>
            )}
          </span>
          <svg
            className={`w-3.5 h-3.5 text-gray-500 transition-transform ${showBillViewer ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {showBillViewer && (
          <div className="px-4 py-3 space-y-3 bg-gray-800 border-t border-gray-700">
            <AttachmentsPanel3 order={{ attachments: allDocs }} />

            {allDocs.length === 0 && (
              <p className="text-xs text-gray-600">
                {linkedBill
                  ? "No files attached yet."
                  : "No bill linked — files will be stored on this MO."}
              </p>
            )}

            <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-gray-700">
              <select
                value={billAttachmentType}
                onChange={(e) => setBillAttachmentType(e.target.value)}
                disabled={attachmentUploading}
                className="px-2 py-1.5 text-xs bg-gray-700 text-gray-300 border border-gray-600 rounded-lg focus:border-orange-500 focus:outline-none disabled:opacity-40"
              >
                <option
                  value="sales_order"
                  className="bg-gray-700 text-gray-300"
                >
                  Sales order
                </option>
                <option
                  value="packing_slip"
                  className="bg-gray-700 text-gray-300"
                >
                  Packing slip
                </option>
              </select>

              <label
                className={cls(
                  "px-3 py-1.5 text-xs font-medium rounded-lg border cursor-pointer transition-colors",
                  attachmentUploading
                    ? "bg-gray-700 border-gray-600 text-gray-600 cursor-not-allowed"
                    : "bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 hover:text-white",
                )}
              >
                {attachmentUploading ? (
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 border border-gray-500 border-t-orange-400 rounded-full animate-spin" />
                    Uploading…
                  </span>
                ) : (
                  "↑ Choose file(s)"
                )}
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  disabled={attachmentUploading}
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length) {
                      handleUploadMOAttachments(files, billAttachmentType);
                      e.target.value = "";
                    }
                  }}
                />
              </label>

              <span className="text-[10px] text-gray-600">
                PDF, JPG, PNG accepted
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Line Items */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="bg-gray-900 px-5 py-3 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">
            Line Items
            <span className="ml-2 text-xs text-gray-500 font-normal">
              {(order.items || []).length} items · ${fmt(totals.items)}
            </span>
          </h2>
          <div className="flex items-center gap-2">
            {canAddItems && (
              <button
                onClick={() => setShowAddItems(true)}
                className="px-3 py-1.5 text-xs font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-500 transition-colors"
              >
                + Add Items
              </button>
            )}
            {canDeliver && (
              <button
                onClick={handleDeliver}
                disabled={delivering}
                className={cls(
                  "px-4 py-1.5 text-sm font-semibold rounded-lg text-white transition-colors",
                  delivering
                    ? "bg-gray-600 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700",
                )}
              >
                {delivering ? "Recording…" : "Record Delivery"}
              </button>
            )}
            {canRevert && (
              <button
                onClick={handleRevertDelivery}
                disabled={saving || delivering}
                className="px-3 py-1.5 text-sm font-semibold rounded-lg text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 transition-colors"
              >
                Revert Delivery
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-900/60">
              <tr>
                <th className="px-3 py-2 text-left text-gray-400 font-medium">
                  Code
                </th>
                <th className="px-3 py-2 text-left text-gray-400 font-medium min-w-[140px]">
                  Description
                </th>
                {/* Size — editable for SF/LF items only */}
                <th className="px-3 py-2 text-left text-gray-400 font-medium w-28">
                  Size
                  {canAddItems && (
                    <span className="ml-1 text-teal-500/60 font-normal text-[9px] normal-case">
                      edit
                    </span>
                  )}
                </th>
                {/* Coverage — read-only, shown only when order has SF/LF items */}
                {hasCovItems && (
                  <th className="px-3 py-2 text-center text-gray-400 font-medium w-32">
                    Coverage
                  </th>
                )}
                <th className="px-3 py-2 text-right text-gray-400 font-medium w-20">
                  Ordered
                  {canAddItems && (
                    <span className="ml-1 text-teal-500/60 font-normal text-[9px] normal-case">
                      edit
                    </span>
                  )}
                </th>
                <th className="px-3 py-2 text-right text-gray-400 font-medium">
                  Delivered
                </th>
                <th className="px-3 py-2 text-right text-gray-400 font-medium">
                  Pending
                </th>

                <th className="px-3 py-2 text-center text-gray-400 font-medium">
                  UOM
                </th>
                <th className="px-3 py-2 text-right text-gray-400 font-medium w-28">
                  {hasCovItems ? "Rate" : "Unit Price"}
                  {canAddItems && (
                    <span className="ml-1 text-teal-500/60 font-normal text-[9px] normal-case">
                      edit
                    </span>
                  )}
                  <span className="block text-[9px] text-gray-600 font-normal normal-case">
                    {hasCovItems ? "$/SF·LF" : "$/PC"}
                  </span>
                </th>
                {/* Per-PC — computed, shown only when SF/LF items present */}
                {hasCovItems && (
                  <th className="px-3 py-2 text-right text-gray-400 font-medium w-24">
                    $/PC
                  </th>
                )}
                <th className="px-3 py-2 text-right text-gray-400 font-medium">
                  Total
                </th>
                <th className="px-3 py-2 text-left text-gray-400 font-medium">
                  Section
                </th>
                <th className="px-3 py-2 text-left text-gray-400 font-medium">
                  Bid Area
                </th>
                {canAddItems && (
                  <th className="px-3 py-2 w-24 text-center text-gray-400 font-medium">
                    Actions
                  </th>
                )}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-700/50">
              {(order.items || []).map((item, i) => {
                const isCov = isCoverageBased(item.uom);
                const storedCoverage = parseFloat(item.coveragePerUnit) || 1;

                // Current edit values
                const editSize = getLE(item._id, "size", item.size || "");
                const editRate = getLE(
                  item._id,
                  "rate",
                  isCov
                    ? (item.unitPrice || 0) / storedCoverage
                    : item.unitPrice || 0,
                );
                const editQty = getLE(item._id, "qty", item.quantity || 0);

                // Coverage: always derived from the current size string (read-only on screen)
                let liveCoverage = storedCoverage;
                let coverageParsed = false;
                if (isCov) {
                  const parsed = parseSizeCoverage(editSize, item.uom);
                  if (parsed !== null) {
                    liveCoverage = parsed;
                    coverageParsed = true;
                  }
                }

                // Live per-PC price from current edits
                const livePerPc = isCov
                  ? calcPackageCost(editRate, liveCoverage)
                  : parseFloat(editRate) || 0;

                // Dirty check
                const storedRate = isCov
                  ? (item.unitPrice || 0) / storedCoverage
                  : item.unitPrice || 0;

                const isDirty = canAddItems && isItemDirty(item);

                return (
                  <tr
                    key={item._id || i}
                    className={
                      i % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20"
                    }
                  >
                    <td className="px-3 py-2 font-mono text-orange-400">
                      {item.itemCode || "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-200">
                      {item.description || "—"}
                    </td>

                    {/* Size — editable for all items when order is editable; coverage feedback only for SF/LF */}
                    <td className="px-3 py-2">
                      {canAddItems ? (
                        <div className="flex flex-col gap-0.5">
                          <input
                            type="text"
                            value={editSize}
                            onChange={(e) =>
                              setLE(item._id, "size", e.target.value)
                            }
                            className={`w-48 px-2 py-2 text-sm bg-gray-700 rounded text-gray-200 focus:outline-none border ${
                              isCov
                                ? editSize
                                  ? coverageParsed
                                    ? "border-green-600/60 focus:border-orange-500"
                                    : "border-orange-600/60 focus:border-orange-500"
                                  : "border-gray-600 focus:border-orange-500"
                                : "border-gray-600 focus:border-orange-500"
                            }`}
                            placeholder={
                              isCov
                                ? item.uom?.toUpperCase().includes("SF")
                                  ? "e.g. 4'x8'"
                                  : "e.g. 10'"
                                : "optional"
                            }
                          />
                          {isCov && editSize && !coverageParsed && (
                            <span className="text-[9px] text-orange-400">
                              Can't parse
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">
                          {item.size || "—"}
                        </span>
                      )}
                    </td>

                    {/* Coverage — read-only, computed from size */}
                    {hasCovItems && (
                      <td className="px-3 py-2 text-center">
                        {isCov ? (
                          <span
                            className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                              coverageParsed
                                ? "bg-blue-900/30 border-blue-700/40 text-blue-200"
                                : "bg-gray-700/40 border-gray-600 text-gray-500"
                            }`}
                          >
                            {coverageParsed
                              ? `${liveCoverage} ${item.uom}`
                              : `${storedCoverage} ${item.uom}`}
                          </span>
                        ) : (
                          <span className="text-gray-700">—</span>
                        )}
                      </td>
                    )}

                    {/* Ordered qty */}
                    <td className="px-3 py-2 text-right">
                      {canAddItems ? (
                        <input
                          type="number"
                          value={editQty}
                          min="0"
                          onChange={(e) =>
                            setLE(item._id, "qty", e.target.value)
                          }
                          className="w-28 px-2 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 text-right focus:border-orange-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      ) : (
                        <span className="text-gray-300 font-mono">
                          {item.quantity || 0}
                        </span>
                      )}
                    </td>

                    <td className="px-3 py-2 text-right text-green-300 font-mono">
                      {item.qtyDelivered || 0}
                    </td>
                    <td className="px-3 py-2 text-right text-yellow-300 font-mono">
                      {item.qtyPending || 0}
                    </td>

                    {/* UOM */}
                    <td className="px-3 py-2 text-center text-gray-200">
                      {isCov ? (
                        <div className="flex flex-col items-center">
                          <span className="font-semibold">PC</span>
                          <span className="text-[9px] text-gray-500">
                            {item.uom}/PC
                          </span>
                        </div>
                      ) : (
                        item.uom || "—"
                      )}
                    </td>

                    {/* Rate — editable */}
                    <td className="px-3 py-2 text-right font-mono">
                      {canAddItems ? (
                        <div className="flex flex-col items-end gap-0.5">
                          <input
                            type="number"
                            value={editRate}
                            min="0"
                            step="0.0001"
                            onChange={(e) =>
                              setLE(item._id, "rate", e.target.value)
                            }
                            className="w-32 px-2 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 text-right focus:border-orange-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                          <span className="text-[9px] text-gray-600">
                            /{isCov ? item.uom : item.uom || "PC"}
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-end">
                          <span className="text-gray-200">
                            ${fmtRate(storedRate)}
                          </span>
                          <span className="text-[9px] text-gray-600">
                            /{isCov ? item.uom : item.uom || "PC"}
                          </span>
                        </div>
                      )}
                    </td>

                    {/* Per-PC price — read-only computed */}
                    {hasCovItems && (
                      <td className="px-3 py-2 text-right font-mono">
                        {isCov ? (
                          <div className="flex flex-col items-end">
                            <span
                              className={
                                canAddItems
                                  ? "text-teal-300 font-semibold"
                                  : "text-gray-300"
                              }
                            >
                              ${fmtRate(livePerPc)}
                            </span>
                            {coverageParsed && canAddItems && (
                              <span className="text-[9px] text-gray-600">
                                {liveCoverage}×$
                                {fmtRate(parseFloat(editRate) || 0)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-700">—</span>
                        )}
                      </td>
                    )}

                    <td className="px-3 py-2 text-right text-blue-300 font-mono font-semibold">
                      ${fmt(item.total || 0)}
                    </td>
                    <td className="px-3 py-2 text-gray-400 text-[11px]">
                      {item.sectionNumber || "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-400">
                      {item.bidArea || "—"}
                    </td>

                    {canAddItems && (
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {isDirty && (
                            <span
                              title="Unsaved — use Save Changes"
                              className="inline-block w-2 h-2 rounded-full bg-orange-400 animate-pulse"
                            />
                          )}
                          <button
                            onClick={() => handleRemoveItem(item._id)}
                            disabled={saving}
                            className="text-red-500 hover:text-red-400 disabled:opacity-40 text-sm leading-none"
                            title="Remove item"
                          >
                            ✕
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {(order.items || []).length === 0 && (
                <tr>
                  <td
                    colSpan={20}
                    className="px-3 py-6 text-center text-gray-500"
                  >
                    No items on this order.
                  </td>
                </tr>
              )}
            </tbody>

            <tfoot className="bg-gray-900 border-t border-gray-600">
              <tr>
                <td
                  colSpan={canDeliver ? 10 : 9}
                  className="px-3 py-2.5 text-xs text-gray-400"
                >
                  {(order.items || []).length} items
                </td>
                <td className="px-3 py-2.5 text-right text-sm font-bold text-blue-300 font-mono">
                  ${fmt(totals.items)}
                </td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <MaterialOrderTimeline
        orderId={orderId}
        token={localStorage.getItem("token")}
        refreshKey={`${order.status}-${(order.items || []).length}-${order.updatedAt || ""}-${order.sentToVendor}`}
      />

      <BudgetImpactPanel
        poId={order.purchaseOrder?._id || order.purchaseOrder}
        orderRows={liveOrderRows}
        refreshKey={`${order.status}-${(order.items || []).length}-${order.updatedAt || ""}`}
        title="Budget Impact (this PO)"
      />
      {/* Surcharges */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">Surcharges</h2>
          <button
            onClick={addSurcharge}
            className="px-3 py-1 text-xs font-medium bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded-lg hover:bg-orange-500/30 transition-colors"
          >
            + Add Surcharge
          </button>
        </div>

        {/* Quick-add suggestions from past orders */}
        {suggestedSurcharges.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">
              Quick-add from past orders
            </p>
            <div className="flex flex-wrap gap-1.5">
              {suggestedSurcharges.map((sugg, i) => {
                const alreadyAdded = surcharges.some(
                  (s) =>
                    (s.description || "").toLowerCase() ===
                      sugg.description.toLowerCase() &&
                    safeNum(s.price ?? s.amount) === sugg.price,
                );
                return (
                  <button
                    key={i}
                    onClick={() => addSuggestedSurcharge(sugg)}
                    disabled={alreadyAdded}
                    className={cls(
                      "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] border transition-all",
                      alreadyAdded
                        ? "bg-gray-700/30 border-gray-700 text-gray-600 cursor-default"
                        : "bg-gray-700/60 border-gray-600 text-gray-300 hover:border-orange-500/60 hover:text-orange-300 hover:bg-orange-500/10 cursor-pointer",
                    )}
                  >
                    <span>{sugg.description}</span>
                    <span
                      className={cls(
                        "font-mono font-semibold",
                        alreadyAdded ? "text-gray-600" : "text-orange-400",
                      )}
                    >
                      ${fmt(sugg.price)}
                    </span>
                    {alreadyAdded ? (
                      <span className="text-gray-600">✓</span>
                    ) : (
                      <span className="text-gray-500">+</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Current surcharge rows */}
        {surcharges.length === 0 && (
          <p className="text-xs text-gray-500">
            No surcharges yet.{" "}
            {suggestedSurcharges.length === 0 &&
              "Add one with the button above."}
          </p>
        )}

        <div className="space-y-2">
          {surcharges.map((s, i) => {
            const isManagedCredit = String(s.description || "").startsWith(
              "Credit —",
            );

            if (isManagedCredit) {
              return (
                <div
                  key={i}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg bg-amber-950/20 border border-amber-800/30"
                >
                  <span className="flex-shrink-0 px-1.5 py-0.5 text-[9px] font-bold rounded bg-amber-900/50 text-amber-400 uppercase tracking-wide">
                    Auto Credit
                  </span>
                  <span className="flex-1 text-xs text-amber-300 truncate">
                    {s.description}
                  </span>
                  <span className="text-xs font-mono font-semibold text-amber-400 tabular-nums">
                    −${fmt(Math.abs(safeNum(s.price ?? s.amount)))}
                  </span>
                  <span className="text-[10px] text-gray-600 italic flex-shrink-0">
                    managed by bill
                  </span>
                </div>
              );
            }

            return (
              <div
                key={i}
                className="flex items-center gap-2 bg-gray-700/30 rounded-lg px-3 py-2 border border-gray-700/50"
              >
                <input
                  type="text"
                  value={s.description}
                  onChange={(e) =>
                    updateSurcharge(i, "description", e.target.value)
                  }
                  placeholder="Description (e.g. Fuel surcharge)"
                  className="flex-1 min-w-0 bg-transparent text-xs text-gray-200 placeholder-gray-600 focus:outline-none focus:placeholder-gray-500 border-b border-transparent focus:border-gray-500 transition-colors py-0.5"
                />
                <div className="flex items-center gap-1 flex-shrink-0">
                  <span className="text-xs text-gray-500">$</span>
                  <NumericFormat
                    value={s.price ?? s.amount ?? 0}
                    onValueChange={(v) =>
                      updateSurcharge(i, "price", v.floatValue ?? 0)
                    }
                    allowNegative
                    decimalScale={2}
                    fixedDecimalScale
                    thousandSeparator
                    className="w-24 bg-transparent text-xs text-right text-gray-200 focus:outline-none border-b border-transparent focus:border-gray-500 transition-colors py-0.5 tabular-nums"
                  />
                </div>
                <button
                  onClick={() => removeSurcharge(i)}
                  className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded text-gray-600 hover:text-red-400 hover:bg-red-900/20 transition-colors text-xs"
                  title="Remove"
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      </div>
      {/* Order Total */}
      <div className="flex justify-between items-center bg-gray-800 rounded-xl border border-gray-700 px-6 py-4">
        <span className="text-sm text-gray-400">
          {(order.items || []).length} line items + {surcharges.length}{" "}
          surcharge{surcharges.length !== 1 ? "s" : ""}
        </span>
        <div className="text-right">
          <p className="text-xs text-gray-500">Order Total</p>
          <p className="text-2xl font-bold text-orange-400 font-mono">
            ${fmt(totals.total)}
          </p>
        </div>
      </div>
      {showBillReviewPanel && linkedBill && (
        <BillMOReviewPanel
          bill={linkedBill}
          projectId={projectId}
          onClose={() => setShowBillReviewPanel(false)}
        />
      )}
      {/* Email modal */}
      {showEmailForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6 space-y-4">
            <h2 className="text-base font-semibold text-orange-400">
              Send Material Order
            </h2>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">From</label>
              <input
                value={userEmail}
                disabled
                className="w-full px-3 py-1.5 text-sm rounded border border-gray-600 bg-gray-700/50 text-gray-400"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">To *</label>
              <input
                value={emailForm.to}
                onChange={(e) =>
                  setEmailForm((p) => ({ ...p, to: e.target.value }))
                }
                placeholder="vendor@example.com"
                className="w-full px-3 py-1.5 text-sm rounded border border-gray-600 bg-gray-700 text-gray-200 focus:border-orange-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">
                Subject *
              </label>
              <input
                value={emailForm.subject}
                onChange={(e) =>
                  setEmailForm((p) => ({ ...p, subject: e.target.value }))
                }
                className="w-full px-3 py-1.5 text-sm rounded border border-gray-600 bg-gray-700 text-gray-200 focus:border-orange-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Body</label>
              <textarea
                rows={5}
                value={emailForm.body}
                onChange={(e) =>
                  setEmailForm((p) => ({ ...p, body: e.target.value }))
                }
                className="w-full px-3 py-2 text-sm rounded border border-gray-600 bg-gray-700 text-gray-200 focus:border-orange-500 focus:outline-none resize-none"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">
                Signature
              </label>
              <input
                value={emailForm.signature}
                onChange={(e) =>
                  setEmailForm((p) => ({ ...p, signature: e.target.value }))
                }
                className="w-full px-3 py-1.5 text-sm rounded border border-gray-600 bg-gray-700 text-gray-200 focus:border-orange-500 focus:outline-none"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => {
                  setShowEmailForm(false);
                  setVendorPdfBuffer(null);
                  setSitePdfBuffer(null);
                }}
                className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEmailSubmit}
                disabled={!vendorPdfBuffer}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"
              >
                Send Email
              </button>
            </div>
          </div>
        </div>
      )}

      {showSiteUpdateForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6 space-y-4">
            <h2 className="text-base font-semibold text-cyan-400">
              Send Site Update
            </h2>
            <p className="text-xs text-gray-500">
              Goes to field contacts, PM, foreman, admins, and delivery driver
              (if pickup). Site PDF is attached automatically. Vendor is not
              emailed.
            </p>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">
                Subject *
              </label>
              <input
                value={siteUpdateForm.subject}
                onChange={(e) =>
                  setSiteUpdateForm((p) => ({ ...p, subject: e.target.value }))
                }
                className="w-full px-3 py-1.5 text-sm rounded border border-gray-600 bg-gray-700 text-gray-200 focus:border-cyan-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">
                Update Message *
              </label>
              <textarea
                rows={5}
                value={siteUpdateForm.message}
                onChange={(e) =>
                  setSiteUpdateForm((p) => ({ ...p, message: e.target.value }))
                }
                placeholder="e.g. Delivery pushed to Friday, quantities adjusted on drywall…"
                className="w-full px-3 py-2 text-sm rounded border border-gray-600 bg-gray-700 text-gray-200 placeholder-gray-500 focus:border-cyan-500 focus:outline-none resize-none"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => {
                  setShowSiteUpdateForm(false);
                  setSiteUpdateBuffer(null);
                }}
                className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendSiteUpdate}
                disabled={sendingSiteUpdate || !siteUpdateBuffer}
                className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 disabled:opacity-50 transition-colors"
              >
                {sendingSiteUpdate ? "Sending…" : "Send Update"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
